import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  FormSectionSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type McaAccompanimentSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["mentalCapacityAct"]["formDefinition"]["sections"][3]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function McaAccompanimentFormSkeleton() {
  return <MultiPageFormSkeleton headerText={tk("mcaAccompaniment.title")} />;
}

///

export type McaAccompanimentFormProps<T extends WorkflowVersion> = Prettify<
  {
    workflowVersion: T;
  } & Pick<
    MultiPageFormProps<McaAccompanimentSchema<T>>,
    | "initialValues"
    | "incidentWorkItem"
    | "onAutoSave"
    | "onSubmit"
    | "onCancel"
  >
>;

///

export function McaAccompanimentForm<T extends WorkflowVersion>(
  props: McaAccompanimentFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const section = workflow?.["mentalCapacityAct"].formDefinition.sections.find(
    (section) => section.id === FormSectionSchema.enum.mcaAccompaniment,
  );
  const formSpecification = section?.buildFormSpecification({
    incidentWorkItem: props.incidentWorkItem,
  });
  const formSchema = formSpecification?.formSchema;
  const strictFieldSchemas = formSpecification?.strictFieldSchemas;

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(() => {
    if (!section || !strictFieldSchemas || !formSchema) {
      return {};
    } else {
      return createInitialValues(strictFieldSchemas, props.initialValues);
    }
  }, [props, strictFieldSchemas, section, formSchema]);

  if (!section || !formSchema || !strictFieldSchemas) {
    return <Error />;
  }

  return (
    <MultiPageForm<typeof formSchema>
      headerText={tk("mcaAccompaniment.title")}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onAutoSave={props.onAutoSave}
    />
  );
}
